import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { InvoiceStatus } from "@rentorng/core";
import { api } from "@/lib/api";
import { ExportButton } from "@/components/export-button";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { InvoiceStatusBadge } from "@/components/status";
import { Button, Card, EmptyState, ErrorState, Input, LinkButton, PageHeader, Spinner, Table, Tabs } from "@/components/ui";

interface Row {
  id: string;
  number: string;
  status: string;
  issueDate: string;
  dueDate: string;
  total: number;
  paid: number;
  partyId: string;
  partyName: string;
  partyEmail: string | null;
  unitLabel: string | null;
  propertyName: string | null;
}

export function InvoicesPage() {
  const [params, setParams] = useSearchParams();
  const bucket = params.get("bucket") ?? "";
  const q = params.get("q") ?? "";
  const partyId = params.get("partyId") ?? "";
  const { vocab, can } = useSession();
  const fmt = useFormat();
  const [selected, setSelected] = useState<string[]>([]);

  const list = useListParams();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["invoices", bucket, list.q, partyId, list.page],
    queryFn: () =>
      api.get<{ invoices: Row[]; today: string; graceDays: number; page: PageMeta }>("/invoices", {
        bucket,
        q: list.q,
        partyId,
        page: list.page,
      }),
    placeholderData: (previous) => previous,
  });

  const rows = data?.invoices ?? [];
  const outstanding = rows.reduce((sum, row) => sum + (row.total - row.paid), 0);

  const tabs = [
    { to: "/app/invoices", label: "All" },
    { to: "/app/invoices?bucket=overdue", label: "Overdue" },
    { to: "/app/invoices?bucket=due_soon", label: "Due soon" },
    { to: "/app/invoices?bucket=upcoming", label: "Upcoming" },
  ];
  const current = bucket ? `/app/invoices?bucket=${bucket}` : "/app/invoices";

  const toggle = (id: string) =>
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  return (
    <div>
      <PageHeader
        title={titleCase(vocab.invoices)}
        subtitle={`${rows.length} shown · ${fmt.money(outstanding)} outstanding`}
        description={"What has been billed and what is still owed. Filter by what is overdue or falling due, chase a selection by email, or raise a charge outside the rent schedule."}
        actions={
          <>
            {/* The list as it stands, filter and all. It replaced a button calling
                `/reports/export`, which writes its own query, ignores every filter on screen, and is
                the shape `routes/data.ts` is deliberately moving away from. */}
            <ExportButton kind="invoices" />
            {can("invoice.edit") ? <LinkButton to="/app/invoices/new" need="invoice.edit">Raise a charge</LinkButton> : null}
            {can("reminder.send") && selected.length > 0 ? (
              <LinkButton to={`/app/reminders/send?invoiceIds=${selected.join(",")}`} need="reminder.send">
                Remind {selected.length} selected
              </LinkButton>
            ) : null}
          </>
        }
      />

      <Tabs tabs={tabs} current={current} />

      <Card padded={false}>
        <div className="border-b border-ink-200 p-3">
          <SearchBox params={list} placeholder="Search number or client…" />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={bucket === "overdue" ? "Nothing is overdue. " : `No ${vocab.invoices} here.`}
            hint={bucket === "overdue" ? "That is the whole point of the product working." : undefined}
          />
        ) : (
          <Table
            head={
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    className="size-4 rounded border-ink-300"
                    checked={selected.length === rows.length && rows.length > 0}
                    onChange={(event) => setSelected(event.target.checked ? rows.map((row) => row.id) : [])}
                  />
                </th>
                <th>Number</th>
                <th>Client</th>
                <th>{titleCase(vocab.unit)}</th>
                <th>Due</th>
                <th>Status</th>
                <th className="num">Total</th>
                <th className="num">Balance</th>
              </tr>
            }
          >
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Select ${row.number}`}
                    className="size-4 rounded border-ink-300"
                    checked={selected.includes(row.id)}
                    onChange={() => toggle(row.id)}
                  />
                </td>
                <td><Link to={`/app/invoices/${row.id}`} className="link">{row.number}</Link></td>
                <td>
                  <Link to={`/app/clients/${row.partyId}`} className="text-ink-700 hover:underline">{row.partyName}</Link>
                  {!row.partyEmail ? <span className="ml-2 text-xs text-warn-700">no email</span> : null}
                </td>
                <td className="text-ink-500">
                  {row.unitLabel ?? "—"}
                  {row.propertyName ? <span className="block text-xs text-ink-400">{row.propertyName}</span> : null}
                </td>
                <td className="text-ink-500">{fmt.date(row.dueDate)}</td>
                <td>
                  <InvoiceStatusBadge
                    invoice={{ dueDate: row.dueDate, total: row.total, paid: row.paid, status: row.status as InvoiceStatus }}
                    asOf={data?.today ?? ""}
                    graceDays={data?.graceDays ?? 0}
                  />
                </td>
                <td className="num tabular">{fmt.money(row.total)}</td>
                <td className="num tabular font-medium">{fmt.money(row.total - row.paid)}</td>
              </tr>
            ))}
          </Table>
        )}

        <Pagination meta={data?.page} params={list} noun={"invoices"} />
      </Card>
    </div>
  );
}
